import { useCallback } from 'react';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

/**
 * Hook that returns a memoized function to dynamically check if the unified thread feature flag is enabled.
 * Useful when you need to check the flag conditionally or with different location IDs at different times.
 *
 * @param locationIds - Optional array of location IDs as default for the returned function.
 * @returns Memoized function that accepts optional locationIds and returns boolean for feature flag status.
 *
 * Fallback chain for location IDs:
 * 1. scopedLocationIds (passed to returned function)
 * 2. locationIds (passed to hook)
 * 3. appScopedLocationIds (from app scope)
 *
 */
export const useGetUnifiedThreadEnabled = (locationIds?: string[]) => {
  const { selectedLocationIds: appScopedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();

  const checkUnifiedThreadEnabled = useCallback(
    (scopedLocationIds?: string[]) => {
      const resolvedLocationIds = !!scopedLocationIds?.filter(Boolean).length
        ? scopedLocationIds
        : !!locationIds?.filter(Boolean).length
        ? locationIds
        : appScopedLocationIds;

      return getFeatureFlagValue({
        flagName: 'nwx:unified-thread',
        locationIds: resolvedLocationIds,
        strategy: 'ANY',
      });
    },
    [locationIds, appScopedLocationIds, getFeatureFlagValue]
  );

  return checkUnifiedThreadEnabled;
};

/**
 * Hook that returns a boolean indicating if the unified thread feature flag is enabled.
 *
 * @param locationIds - Optional array of location IDs as default for the returned function.
 * @returns if unified thread feature flag is enabled.
 *
 * Fallback chain for location IDs:
 * 1. locationIds (passed to hook)
 * 2. appScopedLocationIds (from app scope)
 */
export const useIsUnifiedThreadEnabled = (locationIds?: string[]) => {
  const { selectedLocationIds: appScopedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();

  const resolvedLocationIds = !!locationIds?.filter(Boolean).length ? locationIds : appScopedLocationIds;

  return getFeatureFlagValue({
    flagName: 'nwx:unified-thread',
    locationIds: resolvedLocationIds,
    strategy: 'ANY',
  });
};
